import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme, useDataset } from "../DataContext";
import { canAccess, ROLE_LABELS, ROLE_COLORS } from "../auth";

const NAV_ITEMS = [
  { path: "/platform-map",   label: "Platform Map",             icon: "◎", desc: "Capabilities Reference",  page: "P-00" },
  { path: "/portfolio",      label: "Portfolio Overview",       icon: "◈", desc: "Strategic Health",       page: "P-01" },
  { path: "/health",         label: "Project Health",           icon: "⬡", desc: "Failure Prediction",     page: "P-02" },
  { path: "/benefits",       label: "Benefits Realization",     icon: "◎", desc: "ROI & Value Tracking",   page: "P-03" },
  { path: "/risk",           label: "Risk Intelligence",        icon: "⚠", desc: "Quantified Risk",        page: "P-04" },
  { path: "/resources",      label: "Resource Intelligence",    icon: "◉", desc: "Bottleneck Prediction",  page: "P-05" },
  { path: "/financial",      label: "Financial Performance",    icon: "▦", desc: "Budget & Burn Rate",     page: "P-06" },
  { path: "/schedule",       label: "Schedule & Critical Path", icon: "◧", desc: "Milestone Intelligence", page: "P-07" },
  { path: "/dependencies",   label: "Interdependency Risk",     icon: "⬟", desc: "Cascade Risk",           page: "P-08" },
  { path: "/workforce",      label: "PM Workforce Quality",     icon: "◫", desc: "PM Performance",         page: "P-09" },
  { path: "/rationalization",label: "Portfolio Rationalization",icon: "◬", desc: "Kill/Continue/Redirect", page: "P-10" },
  { path: "/executive",      label: "Executive Board View",     icon: "★", desc: "Eagle-Eye Dashboard",    page: "P-11" },
  { path: "/ai-agent",       label: "AI Agent Console",         icon: "◑", desc: "Natural Language Query", page: "P-12" },
  { path: "/registry",      label: "Portfolio Registry",       icon: "◈", desc: "P3I Hierarchy & Data",   page: "P-13" },
  { path: "/reports",       label: "Reports Centre",           icon: "◇", desc: "Generate & Download",    page: "P-14" },
];

const ADMIN_ITEMS = [
  { path: "/users",    label: "User Management", icon: "◩", desc: "Accounts & Roles" },
  { path: "/settings", label: "Settings",        icon: "⚙", desc: "Platform Config" },
];

export default function Sidebar() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearAll } = useDataset("portfolio");
  const [collapsed, setCollapsed] = useState(false);

  const raw = sessionStorage.getItem("pmo_user");
  const user = raw ? JSON.parse(raw) : null;
  const role = user?.role || "viewer";

  const handleLogout = () => {
    sessionStorage.removeItem("pmo_user");
    clearAll();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const sidebarWidth = collapsed ? "60px" : "260px";

  return (
    <div style={{
      width: sidebarWidth,
      minWidth: sidebarWidth,
      height: "100vh",
      background: theme.sidebar,
      borderRight: `1px solid ${theme.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      transition: "width 0.25s ease, min-width 0.25s ease",
      position: "relative",
      zIndex: 100,
      overflow: "hidden",
    }}>
      {/* Brand Header */}
      <div style={{
        padding: collapsed ? "18px 10px" : "20px 16px",
        borderBottom: `1px solid ${theme.cardBorder}`,
        background: `linear-gradient(135deg, ${theme.accent}10, transparent)`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Shuriken Icon */}
          <div style={{
            width: "32px", height: "32px", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", color: theme.accent,
            filter: `drop-shadow(0 0 8px ${theme.accent}88)`,
          }}>✦</div>
          {!collapsed && (
            <div>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "13px", fontWeight: 700, color: theme.accent,
                letterSpacing: "1px", lineHeight: 1.2,
              }}>
                SHURIKEN
              </div>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: "9px", color: theme.textMuted,
                letterSpacing: "2px", textTransform: "uppercase",
              }}>
                PMO INTELLIGENT
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          position: "absolute", top: "16px", right: "-10px",
          width: "20px", height: "20px",
          background: theme.card, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "50%", cursor: "pointer",
          color: theme.textMuted, fontSize: "10px",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 101,
        }}
      >{collapsed ? "▶" : "◀"}</button>

      {/* User Info */}
      {!collapsed && user && (
        <div style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${theme.cardBorder}44`,
        }}>
          <div style={{ fontSize: "12px", color: theme.textPrimary, fontWeight: 600 }}>
            {user.name}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px" }}>
            <span style={{
              fontSize: "10px", padding: "1px 8px", borderRadius: "10px",
              background: `${ROLE_COLORS[role]}22`,
              color: ROLE_COLORS[role],
              border: `1px solid ${ROLE_COLORS[role]}44`,
              fontWeight: 600, letterSpacing: "0.3px",
            }}>
              {ROLE_LABELS[role] || role.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {!collapsed && (
          <div style={{
            padding: "6px 16px 4px",
            fontSize: "9px", color: theme.textMuted,
            letterSpacing: "1.5px", textTransform: "uppercase",
            fontWeight: 700,
          }}>Analytical Modules</div>
        )}

        {NAV_ITEMS.filter(item => canAccess(role, item.path)).map(item => (
          <NavItem
            key={item.path}
            item={item}
            active={isActive(item.path)}
            collapsed={collapsed}
            theme={theme}
            onClick={() => navigate(item.path)}
          />
        ))}

        {(canAccess(role, "/users") || canAccess(role, "/settings")) && !collapsed && (
          <div style={{
            padding: "10px 16px 4px",
            fontSize: "9px", color: theme.textMuted,
            letterSpacing: "1.5px", textTransform: "uppercase",
            fontWeight: 700, marginTop: "4px",
            borderTop: `1px solid ${theme.cardBorder}44`,
          }}>Administration</div>
        )}

        {ADMIN_ITEMS.filter(item => canAccess(role, item.path)).map(item => (
          <NavItem
            key={item.path}
            item={item}
            active={isActive(item.path)}
            collapsed={collapsed}
            theme={theme}
            onClick={() => navigate(item.path)}
          />
        ))}
      </nav>

      {/* System Status */}
      {!collapsed && (
        <SystemStatus theme={theme} />
      )}

      {/* Logout */}
      <div style={{ padding: "10px 12px", borderTop: `1px solid ${theme.cardBorder}` }}>
        <button
          onClick={handleLogout}
          style={{
            width: "100%", padding: collapsed ? "8px 0" : "8px 14px",
            background: "transparent",
            border: `1px solid ${theme.danger}33`,
            borderRadius: "6px",
            color: theme.danger,
            cursor: "pointer",
            fontSize: "11px", fontWeight: 600,
            letterSpacing: "0.5px", textTransform: "uppercase",
            display: "flex", alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: "8px",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = `${theme.danger}15`}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <span>⏻</span>
          {!collapsed && "LOGOUT"}
        </button>
      </div>
    </div>
  );
}

function NavItem({ item, active, collapsed, theme, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? item.label : ""}
      style={{
        display: "flex", alignItems: "center",
        gap: "10px",
        padding: collapsed ? "10px 0" : "8px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        margin: "1px 6px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.15s",
        background: active
          ? `linear-gradient(90deg, ${theme.accent}18, ${theme.accent}08)`
          : hovered ? `${theme.accent}08` : "transparent",
        borderLeft: active ? `2px solid ${theme.accent}` : "2px solid transparent",
        position: "relative",
      }}
    >
      <span style={{
        fontSize: "14px",
        color: active ? theme.accent : hovered ? theme.accentText : theme.textMuted,
        flexShrink: 0, width: "16px", textAlign: "center",
        filter: active ? `drop-shadow(0 0 6px ${theme.accent}88)` : "none",
        transition: "color 0.15s",
      }}>{item.icon}</span>

      {!collapsed && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "12px", fontWeight: active ? 600 : 500,
            color: active ? theme.textPrimary : hovered ? theme.textSecondary : theme.textSecondary,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{item.label}</div>
          <div style={{
            fontSize: "10px",
            color: active ? theme.accent : theme.textMuted,
            opacity: 0.8,
          }}>{item.desc}</div>
        </div>
      )}

      {!collapsed && item.page && (
        <span style={{
          fontSize: "9px", padding: "1px 5px",
          background: active ? `${theme.accent}20` : `${theme.cardBorder}80`,
          color: active ? theme.accent : theme.textMuted,
          borderRadius: "4px", flexShrink: 0,
        }}>{item.page}</span>
      )}
    </div>
  );
}

function SystemStatus({ theme }) {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 3000);
    return () => clearInterval(id);
  }, []);

  const cpu    = Math.floor(18 + Math.sin(tick * 0.7) * 12);
  const mem    = Math.floor(42 + Math.sin(tick * 0.4) * 8);
  const status = "CONNECTED";

  return (
    <div style={{
      padding: "10px 14px",
      borderTop: `1px solid ${theme.cardBorder}44`,
      fontSize: "10px",
    }}>
      <div style={{
        fontFamily: "'Share Tech Mono', monospace",
        color: theme.textMuted, marginBottom: "6px",
        letterSpacing: "0.5px", textTransform: "uppercase",
      }}>System Status</div>
      {[
        { label: "CPU",    value: `${cpu}%`,   color: cpu > 80 ? theme.danger : theme.success },
        { label: "MEM",    value: `${mem}%`,   color: mem > 80 ? theme.warning : theme.success },
        { label: "STATUS", value: status, color: theme.success },
      ].map(s => (
        <div key={s.label} style={{
          display: "flex", justifyContent: "space-between",
          marginBottom: "2px",
        }}>
          <span style={{ color: theme.textMuted, fontFamily: "'Share Tech Mono', monospace" }}>
            {s.label}
          </span>
          <span style={{ color: s.color, fontFamily: "'Share Tech Mono', monospace" }}>
            {s.value}
          </span>
        </div>
      ))}
    </div>
  );
}
